import type { FC } from 'react';
import { memo } from 'react';
import LoadingSkeleton from '@components/LoadingSkeleton';
import { Picture, IconText } from '@ui';
import StoreInfoSchedule from './components/StoreInfoSchedule';
import StoreInfoIsMobilePick from './components/StoreInfoIsMobilePick';
import StoreInfoFastDelivery from './components/StoreInfoFastDelivery';
import icons from '@icons';
import { transformScheduleToStoreInfoSchedule } from '@utils/processSchedule';
import { StoreTypeTitles } from '../shared/storeTypes';
import { formatNearestInterval } from './nearestInterval';
import type { Pickup } from '@root/src/types';
import './index.scss';

const {
  pin: { black: pinFilledBlack },
  phoneMissed: { black: phoneMissed },
  calendar: { black: calendarFilledBlack },
  ruble: { black: rubleFilledBlack }
} = icons.Fill;

interface StoreInfoCardProps {
  store: Pickup | any;
  loading?: boolean;
  isShowAdditionalInfo?: boolean;
  isMobile?: boolean;
  storeAvailableQuantity?: number | null;
  isShowAvailableQuantity?: boolean;
}

const StoreInfoCard: FC<StoreInfoCardProps> = ({
  store,
  loading,
  isShowAdditionalInfo,
  isMobile = false,
  storeAvailableQuantity,
  isShowAvailableQuantity
}) => {
  if (!store?.id && !store?.title && !store?.name) {
    return null;
  }

  const nearestInterval = formatNearestInterval(store);

  return (
    <div className={'StoreInfo'}>
      <div
        className={`StoreInfo__header${
          store?.is_mobile ? ' StoreInfo__header--mb-5' : ''
        } columnFlex`}
      >
        <div className='details'>
          <div className='StoreInfo__heading'>{store.title}</div>
          {!isShowAdditionalInfo && store.fastDelivery && (
            <StoreInfoFastDelivery />
          )}
          {store.type && isShowAvailableQuantity ? (
            storeAvailableQuantity ? (
              <div className='deliveryAvailable'>
                В&nbsp;наличии:&nbsp;
                <span className='deliveryAvailableAmount'>
                  {storeAvailableQuantity}&nbsp;шт
                </span>
              </div>
            ) : (
              <div className='deliveryUnavailable'>
                <span className='deliveryUnavailableText'>
                  Нет&nbsp;в&nbsp;наличии
                </span>
              </div>
            )
          ) : null}
        </div>
        {store.type ? (
          <div className='storeInfoAvailableQuantity'>
            <div className='type-store'>
              {StoreTypeTitles[store.type] || ''}

              {store?.metroStations?.length > 0 && (
                <div className='metro-station-container'>
                  <div
                    className='metro-circle'
                    style={{
                      backgroundColor:
                        store.metroStations[0].line?.color || '#ccc'
                    }}
                  />
                  <span
                    className='metro-station-text'
                    style={{
                      color: store.metroStations[0].line?.color || '#666'
                    }}
                  >
                    {store.metroStations[0].title}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
      <div className='StoreInfo__is-mobile'>
        {!!store?.is_mobile && <StoreInfoIsMobilePick />}
      </div>
      {loading ? (
        <LoadingSkeleton style={{ width: '70%' }} />
      ) : store.address ? (
        <IconText
          className='StoreInfo__address'
          height='16'
          marginRight='12'
          marginBottom='0'
          text={
            store.ya_maps_url ? (
              <a
                href={store.ya_maps_url}
                target='_blank'
                rel='noopener noreferrer'
                className='StoreInfo__address-link'
              >
                {store.address}
              </a>
            ) : (
              store.address
            )
          }
          icon={pinFilledBlack}
        />
      ) : null}
      <StoreInfoSchedule
        className={'StoreInfoSchedule marginLeft'}
        list={transformScheduleToStoreInfoSchedule(store.schedule)}
      />

      {isShowAdditionalInfo && store?.nearestAvailableInterval?.value && (
        <>
          <IconText
            className='StoreInfo__address'
            fontSize='14'
            text={nearestInterval}
            height='16'
            marginRight='12'
            marginBottom='0'
            icon={calendarFilledBlack}
          />
          <div className={'StoreInfoSchedule marginLeft'}>
            Хранится в течение дня доставки
          </div>
        </>
      )}
      {!!store.phone && !isMobile && (
        <div className='StoreInfo__address'>
          <div className='StoreInfo__schedule-wrapper'>
            <Picture
              className='StoreInfo__schedule-icon'
              icon={phoneMissed}
              height='16'
            />
            {loading ? (
              <LoadingSkeleton style={{ width: '70%' }} />
            ) : (
              !!store.phone && (
                <a
                  href={`tel:${store.phone.replace(/\D/g, '')}`}
                  className='StoreInfo__address-link'
                >
                  {store.phone}
                </a>
              )
            )}
          </div>
        </div>
      )}
      {!!store.paymentTypes?.length && !isMobile && (
        <div className='StoreInfo__info'>
          <div className='StoreInfo__schedule-wrapper'>
            <Picture
              className='StoreInfo__schedule-icon'
              icon={rubleFilledBlack}
              height='16'
            />
            {loading ? (
              <LoadingSkeleton style={{ width: '70%' }} />
            ) : (
              !!store.paymentTypes?.length && (
                <span>
                  Способы оплаты:{' '}
                  {store.paymentTypes
                    .map((item: { title: string }) => item.title)
                    .join(', ')}
                </span>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(StoreInfoCard);
